import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Car, Save, Trash2, Plus, ChevronDown, ArrowLeft } from "lucide-react";
import "../css/EditVehicle.css";
import axios from 'axios';

const CLOUD_NAME = "dnriybfwk";
const UPLOAD_PRESET = "ofak_assets";

const getContentType = (file) => {
  if (file.type) return file.type;
  if (file.name.endsWith(".glb")) return "model/gltf-binary";
  if (file.name.endsWith(".pdf")) return "application/pdf";
  return "application/octet-stream";
};

const uploadToS3 = async (file) => {
  // 1. get a signed upload URL from backend
  const { data } = await axios.post(
    "/api/assets/upload-url",
    {
      fileName: file.name,
      contentType: getContentType(file),
    }
  );

  const { uploadUrl, fileUrl } = data.data;

  // 2. upload the file directly to S3 using the signed URL
  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": getContentType(file),
    },
  });

  // 3. return the CDN/public URL to store in form state
  return fileUrl;
};

const uploadToCloudinary = async (file, resourceType = "image") => {
  const data = new FormData();
  data.append("file", file);
  data.append("upload_preset", UPLOAD_PRESET);
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${resourceType}/upload`,
    { method: "POST", body: data }
  );
  const result = await res.json();
  return result.secure_url;
};

const emptyDetail     = () => ({ label: "", value: "" });
const emptyMetric     = () => ({ label: "", value: "" });
const emptyColor      = () => ({ name: "", hex: "#0A2EA0", imageUrl: "", glbUrl: "", imageFile: null, glbFile: null });


export default function EditVehicle() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]         = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [allStates, setAllStates]     = useState([]);

  const [categoryName, setCategoryName] = useState("n-first");
  const [variantName, setVariantName]   = useState("");

  const [colors,      setColors]      = useState([]);
  const [details,     setDetails]     = useState([]);
  const [metrics,     setMetrics]     = useState([]);
  const [brochureUrl, setBrochureUrl]   = useState("");
  const [brochureFile, setBrochureFile] = useState(null);

  //const [editingIndex, setEditingIndex] = useState(null);

//   const [priceColumns, setPriceColumns] = useState([
//   { label: "Ex Showroom Price" },
//   { label: "On Road Price" },
// ]);
const [pricingColumns, setPricingColumns] = useState([]);

const [statePrices, setStatePrices] = useState([]);

//const [allStates, setAllStates] = useState([]);

// const [showPriceModal, setShowPriceModal] = useState(false);

// const [currentStatePricingRows, setCurrentStatePricingRows] = useState([]);

  // ── Fetch states ──
  useEffect(() => {
    fetch("/api/states")
      .then((r) => r.json())
      .then((d) => { if (d.success) setAllStates(d.data); })
      .catch(console.log);
  }, []);

  // ── Fetch existing vehicle ──
  useEffect(() => {
    const loadVehicle = async () => {
      try {
        const res  = await fetch(`/api/vehicles/${id}`);
        const response = await res.json();
        const data = response.data;
        console.log("vehicles detail", data);

        setCategoryName(data.categoryName || "n-first");
        setVariantName(data.variantName   || "");
        setBrochureUrl(data.VehicleBrochure?.pdfUrl || "");

        setColors(
          (data.VehicleColors || []).map((c) => ({
            id:        c.id,
            name:      c.name,
            hex:       c.hex,
            imageUrl:  c.imageUrl,
            glbUrl:    c.glbUrl || "",
            imageFile: null,
            glbFile:   null,
          }))
        );

        setDetails(
          (data.VehicleDetails || []).map((d) => ({ id: d.id, label: d.label, value: d.value }))
        );

        setMetrics(
          (data.VehicleMetrics || []).map((m) => ({ id: m.id, label: m.label, value: m.value }))
        );

        if (data.VehicleStatePrices?.length) {
          const loadedStatePrices = data.VehicleStatePrices.map((sp) => ({
            state_id: String(sp.state_id),
            values: sp.values || {},
          }));
          const savedColumnLabels = [
            ...new Set(
              loadedStatePrices.flatMap((sp) => Object.keys(sp.values || {}))
            ),
          ];

          setStatePrices(loadedStatePrices);

          if (savedColumnLabels.length) {
            //setPriceColumns(savedColumnLabels.map((label) => ({ label })));
            setPricingColumns(savedColumnLabels || []);
          }
        }
      } catch (err) {
        console.error("Failed to load vehicle:", err);
      } finally {
        setLoadingData(false);
      }
    };
    loadVehicle();
  }, [id]);

  // ── Color handlers ──
  const updateColor = (i, key, val) =>
    setColors((prev) => prev.map((c, idx) => idx === i ? { ...c, [key]: val } : c));
  const addColor    = () => setColors((p) => [...p, emptyColor()]);
  const removeColor = (i) => setColors((p) => p.filter((_, idx) => idx !== i));

  // ── Detail handlers ──
  const updateDetail = (i, key, val) =>
    setDetails((prev) => prev.map((d, idx) => idx === i ? { ...d, [key]: val } : d));
  const addDetail    = () => setDetails((p) => [...p, emptyDetail()]);
  const removeDetail = (i) => setDetails((p) => p.filter((_, idx) => idx !== i));

  // ── Metric handlers ──
  const updateMetric = (i, key, val) =>
    setMetrics((prev) => prev.map((m, idx) => idx === i ? { ...m, [key]: val } : m));
  const addMetric    = () => { if (metrics.length < 3) setMetrics((p) => [...p, emptyMetric()]); };
  const removeMetric = (i) => setMetrics((p) => p.filter((_, idx) => idx !== i));

  // ── State price handlers ──
  // const updateStatePrice = (i, key, val) =>
  //   setStatePrices((prev) => prev.map((sp, idx) => idx === i ? { ...sp, [key]: val } : sp));

  // const handleStatePriceStateChange = (i, stateId) =>
  //   setStatePrices((prev) =>
  //     prev.map((sp, idx) => idx === i ? { ...sp, state_id: stateId } : sp)
  //   );

  // const removeStatePrice = (i) => setStatePrices((p) => p.filter((_, idx) => idx !== i));

 // const usedStateIds = statePrices.map((sp) => String(sp.state_id)).filter(Boolean);

   // ── State price handlers ────────────────────────────────────────
  const updateStatePrice = (i, key, val) =>
    setStatePrices((prev) => prev.map((sp, idx) => idx === i ? { ...sp, [key]: val } : sp));

  const handleStatePriceStateChange = (i, stateId) => {
    const found = allStates.find((s) => String(s.id) === String(stateId));
    setStatePrices((prev) =>
      prev.map((sp, idx) =>
        idx === i
          ? { ...sp, state_id: stateId, stateName: found ? found.name : "" }
          : sp
      )
    );
  };

  const addStatePrice = () => setStatePrices((p) => [...p, { state_id: "", stateName: "", values: {}}]);

  const removeStatePrice = (i) =>
    setStatePrices((p) => p.filter((_, idx) => idx !== i));

  // IDs already chosen in other rows (to avoid duplicates)
  const usedStateIds = statePrices.map((sp) => String(sp.state_id)).filter(Boolean);

  const addPricingColumn = () => {
  if (pricingColumns.length >= 7) {
    alert("Maximum 7 columns allowed");
    return;
  }

  const newColumn = `Column ${pricingColumns.length + 1}`;

  setPricingColumns((prev) => [...prev, newColumn]);

  setStatePrices((prev) =>
    prev.map((row) => ({
      ...row,
      values: {
        ...row.values,
        [newColumn]: "",
      },
    }))
  );
};

const updatePricingColumn = (index, value) => {
  const oldName = pricingColumns[index];

  const updated = [...pricingColumns];
  updated[index] = value;
  setPricingColumns(updated);

  setStatePrices((prev) =>
    prev.map((row) => {
      const values = { ...row.values };

      if (oldName in values) {
        values[value] = values[oldName];
        delete values[oldName];
      }

      return { ...row, values };
    })
  );
};

const removePricingColumn = (index) => {
  const columnName = pricingColumns[index];

  setPricingColumns(
    pricingColumns.filter((_, i) => i !== index)
  );

  setStatePrices((prev) =>
    prev.map((row) => {
      const values = { ...row.values };
      delete values[columnName];

      return {
        ...row,
        values
      };
    })
  );
};

const updateStateValue = (
  rowIndex,
  columnName,
  value
) => {
  setStatePrices((prev) =>
    prev.map((row, i) =>
      i === rowIndex
        ? {
            ...row,
            values: {
              ...row.values,
              [columnName]: value
            }
          }
        : row
    )
  );
};

  // const activePriceColumns = pricingColumns
  //   .map((col) => col.trim())
  //   .filter(Boolean);










 
  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (colors.some((c) => !c.name.trim()))                       return alert("Fill all color names");
    if (colors.some((c) => !c.imageUrl && !c.imageFile))          return alert("Each color needs an image");
    if (details.some((d) => !d.label.trim() || !d.value.trim()))  return alert("Fill all specification fields");
    if (metrics.some((m) => !m.label.trim() || !m.value.trim()))  return alert("Fill all metric fields");
    if (statePrices.some((sp) => !sp.state_id))  {
      return alert("Select a state for every pricing row");
    }

    // if (
    //   statePrices.some((sp) => 
    //     priceColumns.some((col) => {
    //       const label = col.label.trim();
    //       return !label || !sp.values?.[label];
    //     })
    //   )
    // ) {
    //   return alert("Fill all prices for every state");
    // }
   

    setLoading(true);
    try {
      const uploadedColors = await Promise.all(
        colors.map(async (c) => ({
          id:       c.id || undefined,
          name:     c.name,
          hex:      c.hex,
          imageUrl: c.imageFile ? await uploadToS3(c.imageFile, "image") : c.imageUrl,
          glbUrl:   c.glbFile   ? await uploadToS3(c.glbFile,   "raw")   : c.glbUrl || null,
        }))
      );

      const finalBrochureUrl = brochureFile
        ? await uploadToS3(brochureFile, "raw")
        : brochureUrl || null;

      const payload = {
        categoryName,
        variantName,
        brochureUrl: finalBrochureUrl,
        colors:  uploadedColors,
        details: details.map(({ label, value, id: did }) => ({ label, value, ...(did ? { id: did } : {}) })),
        metrics: metrics.map(({ label, value, id: mid }) => ({ label, value, ...(mid ? { id: mid } : {}) })),
        statePrices: statePrices.map((sp) => ({
           state_id: Number(sp.state_id),
           //values: normalizePricingValues(sp.values),
           values: sp.values
        })),
      };

      const res = await fetch(`/api/vehicles/${id}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Server error");
      alert("Vehicle updated successfully!");
      navigate("/dashboard");
    } catch (err) {
      alert("Something went wrong. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="ev-loading-screen">
        <div className="ev-spinner" />
        <p>Loading vehicle…</p>
      </div>
    );
  }

  return (
    <div className="ev-page">

      {/* Header */}
      <div className="ev-header">
        <div className="ev-header-left">
          <button className="ev-back-btn" type="button" onClick={() => navigate("/dashboard")}>
            <ArrowLeft size={16} />
          </button>
          <div className="ev-header-icon"><Car size={20} /></div>
          <div>
            <h1 className="ev-title">Edit Vehicle</h1>
            <p className="ev-subtitle">ID #{id} — {variantName || "…"}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="ev-form">

        {/* ── 01 Basic Info ── */}
        <div className="ev-section">
          <div className="ev-section-label">
            <span className="ev-section-num">01</span>
            Basic Information
          </div>
          <div className="ev-section-body">
            <div className="ev-row-2">

              <div className="ev-field">
                <label className="ev-label">Category</label>
                <div className="ev-select-wrap">
                  <select
                    className="ev-select"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                  >
                    <option value="n-first">n-First</option>
                    <option value="diplos">Diplos</option>
                    <option value="institutional">Institutional</option>
                  </select>
                  <ChevronDown size={14} className="ev-select-arrow" />
                </div>
              </div>

              <div className="ev-field">
                <label className="ev-label">Variant Name</label>
                <input
                  className="ev-input"
                  type="text"
                  placeholder="e.g. i-max+"
                  value={variantName}
                  onChange={(e) => setVariantName(e.target.value)}
                  required
                />
              </div>

            </div>
          </div>
        </div>

        {/* ── 02 Colors ── */}
        <div className="ev-section">
          <div className="ev-section-label">
            <span className="ev-section-num">02</span>
            Colors &amp; Assets
            <span className="ev-section-hint">Leave file blank to keep existing image / GLB</span>
          </div>
          <div className="ev-section-body">
            <div className="ev-colors-list">
              {colors.map((c, i) => (
                <div key={i} className="ev-color-card">

                  {c.imageUrl && (
                    <div className="ev-color-thumb">
                      <img src={c.imageUrl} alt={c.name} />
                      <span className="ev-color-thumb-label">Current image</span>
                    </div>
                  )}

                  <div className="ev-color-card-top">
                    <div className="ev-color-swatch-wrap">
                      <input
                        type="color"
                        className="ev-color-picker"
                        value={c.hex}
                        onChange={(e) => updateColor(i, "hex", e.target.value)}
                      />
                      <span className="ev-color-hex">{c.hex}</span>
                    </div>
                    <input
                      className="ev-input ev-color-name"
                      type="text"
                      placeholder="Color name"
                      value={c.name}
                      onChange={(e) => updateColor(i, "name", e.target.value)}
                    />
                    {colors.length > 1 && (
                      <button
                        type="button"
                        className="ev-icon-btn ev-danger"
                        onClick={() => removeColor(i)}
                        title="Remove color"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>

                  <div className="ev-color-files">
                    <div className="ev-file-field">
                      <label className="ev-file-label">
                        Replace Image <span className="ev-optional">optional</span>
                      </label>
                      <label className="ev-file-box">
                        <input
                          type="file"
                          accept="image/*"
                          className="ev-file-hidden"
                          onChange={(e) => updateColor(i, "imageFile", e.target.files[0])}
                        />
                        <span className="ev-file-text">
                          {c.imageFile ? c.imageFile.name : "Choose new image…"}
                        </span>
                        <span className="ev-file-btn">Browse</span>
                      </label>
                    </div>

                    <div className="ev-file-field">
                      <label className="ev-file-label">
                        Replace 3D Model (.glb) <span className="ev-optional">optional</span>
                      </label>
                      <label className="ev-file-box">
                        <input
                          type="file"
                          accept=".glb"
                          className="ev-file-hidden"
                          onChange={(e) => updateColor(i, "glbFile", e.target.files[0])}
                        />
                        <span className="ev-file-text">
                          {c.glbFile ? c.glbFile.name : c.glbUrl ? "Current GLB saved" : "Choose .glb file…"}
                        </span>
                        <span className="ev-file-btn">Browse</span>
                      </label>
                    </div>
                  </div>

                </div>
              ))}
            </div>
            <button type="button" className="ev-add-btn" onClick={addColor}>
              <Plus size={15} /> Add Color
            </button>
          </div>
        </div>

        {/* ── 03 Specifications ── */}
        <div className="ev-section">
          <div className="ev-section-label">
            <span className="ev-section-num">03</span>
            Specifications
            <span className="ev-section-hint">Label–value pairs shown on the product card</span>
          </div>
          <div className="ev-section-body">
            <div className="ev-kv-list">
              {details.map((d, i) => (
                <div key={i} className="ev-kv-row">
                  <input
                    className="ev-input"
                    type="text"
                    placeholder="Label (e.g. Battery Capacity)"
                    value={d.label}
                    onChange={(e) => updateDetail(i, "label", e.target.value)}
                  />
                  <input
                    className="ev-input"
                    type="text"
                    placeholder="Value (e.g. 3 kWh)"
                    value={d.value}
                    onChange={(e) => updateDetail(i, "value", e.target.value)}
                  />
                  {details.length > 1 && (
                    <button type="button" className="ev-icon-btn ev-danger" onClick={() => removeDetail(i)}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" className="ev-add-btn" onClick={addDetail}>
              <Plus size={15} /> Add Specification
            </button>
          </div>
        </div>

        {/* ── 04 Key Metrics ── */}
        <div className="ev-section">
          <div className="ev-section-label">
            <span className="ev-section-num">04</span>
            Key Metrics
            <span className="ev-section-hint">Max 3 highlights</span>
          </div>
          <div className="ev-section-body">
            <div className="ev-kv-list">
              {metrics.map((m, i) => (
                <div key={i} className="ev-kv-row">
                  <input
                    className="ev-input"
                    type="text"
                    placeholder="Label (e.g. IDC Range)"
                    value={m.label}
                    onChange={(e) => updateMetric(i, "label", e.target.value)}
                  />
                  <input
                    className="ev-input"
                    type="text"
                    placeholder="Value (e.g. 109 km)"
                    value={m.value}
                    onChange={(e) => updateMetric(i, "value", e.target.value)}
                  />
                  {metrics.length > 1 && (
                    <button type="button" className="ev-icon-btn ev-danger" onClick={() => removeMetric(i)}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {metrics.length < 3 && (
              <button type="button" className="ev-add-btn" onClick={addMetric}>
                <Plus size={15} /> Add Metric
              </button>
            )}
          </div>
        </div>

       {/* ── 05 State-wise Pricing ── */}
<div className="ev-section">
  <div className="ev-section-label">
    <span className="ev-section-num">05</span>
    State-wise Pricing
    <span className="ev-section-hint">
      Create up to 7 pricing columns and assign values state-wise
    </span>
  </div>

  <div className="ev-section-body">

    {/* Pricing Columns */}
    <div className="ev-pricing-columns">

      <div className="ev-pricing-columns-header">
        <h4>Pricing Columns</h4>
      </div>

      <div className="ev-kv-list">
        {pricingColumns.map((column, index) => (
          <div key={index} className="ev-kv-row">

            <input
              type="text"
              className="ev-input"
              placeholder="Column Name"
              value={column}
              onChange={(e) =>
                updatePricingColumn(index, e.target.value)
              }
            />

            <button
              type="button"
              className="ev-icon-btn ev-danger"
              onClick={() => removePricingColumn(index)}
            >
              <Trash2 size={15} />
            </button>

          </div>
        ))}
      </div>

      {pricingColumns.length < 7 && (
        <button
          type="button"
          className="ev-add-btn"
          onClick={addPricingColumn}
        >
          <Plus size={15} />
          Add Column
        </button>
      )}

    </div>

    {/* Table Header */}
    <div
      className="ev-price-header"
      style={{
        gridTemplateColumns: `220px repeat(${pricingColumns.length}, 1fr) 60px`
      }}
    >
      <span>State</span>

      {pricingColumns.map((column, index) => (
        <span key={index}>
          {column || `Column ${index + 1}`}
        </span>
      ))}

      <span></span>
    </div>

    {/* State Rows */}
    <div className="ev-kv-list">

      {statePrices.map((sp, i) => (
        <div
          key={i}
          className="ev-price-row"
          style={{
            display: "grid",
            gridTemplateColumns: `220px repeat(${pricingColumns.length}, 1fr) 60px`,
            gap: "12px",
            alignItems: "center"
          }}
        >

          <div className="ev-select-wrap">
            <select
              className="ev-select"
              value={sp.state_id}
              onChange={(e) =>
                handleStatePriceStateChange(i, e.target.value)
              }
            >
              <option value="">Select state...</option>

              {allStates.map((s) => (
                <option
                  key={s.id}
                  value={s.id}
                  disabled={
                    usedStateIds.includes(String(s.id)) &&
                    String(sp.state_id) !== String(s.id)
                  }
                >
                  {s.name} ({s.state_code})
                </option>
              ))}
            </select>

            <ChevronDown
              size={14}
              className="ev-select-arrow"
            />
          </div>

          {pricingColumns.map((column, colIndex) => (
            <input
              key={colIndex}
              type="number"
              className="ev-input"
              placeholder={column || "Value"}
              value={sp.values?.[column] || ""}
              onChange={(e) =>
                updateStateValue(
                  i,
                  column,
                  e.target.value
                )
              }
            />
          ))}

          {statePrices.length > 1 && (
            <button
              type="button"
              className="ev-icon-btn ev-danger"
              onClick={() => removeStatePrice(i)}
            >
              <Trash2 size={15} />
            </button>
          )}

        </div>
      ))}

    </div>

    <button
      type="button"
      className="ev-add-btn"
      onClick={addStatePrice}
      disabled={
        statePrices.length >= allStates.length &&
        allStates.length > 0
      }
    >
      <Plus size={15} />
      Add State
    </button>

  </div>
</div>

      

        {/* ── 06 Brochure ── */}
        <div className="ev-section">
          <div className="ev-section-label">
            <span className="ev-section-num">06</span>
            Brochure PDF
            <span className="ev-section-hint">Upload new file to replace existing</span>
          </div>
          <div className="ev-section-body">
            {brochureUrl && !brochureFile && (
              <div className="ev-existing-brochure">
                <a href={brochureUrl} target="_blank" rel="noreferrer">
                  View current brochure ↗
                </a>
              </div>
            )}
            <div className="ev-file-field ev-file-wide">
              <label className="ev-file-label">
                {brochureUrl ? "Replace PDF" : "Upload PDF"}
                <span className="ev-optional"> optional</span>
              </label>
              <label className="ev-file-box">
                <input
                  type="file"
                  accept=".pdf"
                  className="ev-file-hidden"
                  onChange={(e) => setBrochureFile(e.target.files[0])}
                />
                <span className="ev-file-text">
                  {brochureFile ? brochureFile.name : "Choose PDF file…"}
                </span>
                <span className="ev-file-btn">Browse</span>
              </label>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="ev-footer">
          <button type="button" className="ev-cancel-btn" onClick={() => navigate("/dashboard")}>
            Cancel
          </button>
          <button type="submit" className="ev-submit" disabled={loading}>
            {loading ? (
              <><span className="ev-spinner-sm" /> Uploading &amp; Saving…</>
            ) : (
              <><Save size={16} /> Update Vehicle</>
            )}
          </button>
        </div>

      

      </form>
    </div>
  );
}
// import { useState, useEffect } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { Car, Save, Trash2, Plus, ChevronDown, ArrowLeft } from "lucide-react";
// import "../css/EditVehicle.css";

// const CLOUD_NAME = "dnriybfwk";
// const UPLOAD_PRESET = "ofak_assets";

// const uploadToCloudinary = async (file, resourceType = "image") => {
//   const data = new FormData();
//   data.append("file", file);
//   data.append("upload_preset", UPLOAD_PRESET);
//   const res = await fetch(
//     `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${resourceType}/upload`,
//     { method: "POST", body: data }
//   );
//   const result = await res.json();
//   return result.secure_url;
// };

// const emptyDetail = () => ({ label: "", value: "" });
// const emptyMetric = () => ({ label: "", value: "" });
// const emptyColor  = () => ({ name: "", hex: "#0A2EA0", imageUrl: "", glbUrl: "", imageFile: null, glbFile: null });

// export default function EditVehicle() {
//   const { id } = useParams();
//   const navigate = useNavigate();

//   const [loading, setLoading]         = useState(false);
//   const [loadingData, setLoadingData] = useState(true);

//   const [categoryName, setCategoryName] = useState("n-first");
//   const [variantName, setVariantName]   = useState("");
//   const [price, setPrice]               = useState("");

//   const [colors,  setColors]   = useState([]);
//   const [details, setDetails]  = useState([]);
//   const [metrics, setMetrics]  = useState([]);

//   const [brochureUrl, setBrochureUrl]   = useState("");
//   const [brochureFile, setBrochureFile] = useState(null);

//   // ── Fetch existing vehicle ──
//   useEffect(() => {
//     const loadVehicle = async () => {
//       try {
//         const res  = await fetch(`http://localhost:5000/api/vehicles/${id}`);
//         const data = await res.json();

//         setCategoryName(data.categoryName || "n-first");
//         setVariantName(data.variantName   || "");
//         setPrice(data.price               || "");
//         setBrochureUrl(data.VehicleBrochure?.pdfUrl || "");

//         setColors(
//           (data.VehicleColors || []).map((c) => ({
//             id:        c.id,
//             name:      c.name,
//             hex:       c.hex,
//             imageUrl:  c.imageUrl,
//             glbUrl:    c.glbUrl || "",
//             imageFile: null,
//             glbFile:   null,
//           }))
//         );

//         setDetails(
//           (data.VehicleDetails || []).map((d) => ({ id: d.id, label: d.label, value: d.value }))
//         );

//         setMetrics(
//           (data.VehicleMetrics || []).map((m) => ({ id: m.id, label: m.label, value: m.value }))
//         );
//       } catch (err) {
//         console.error("Failed to load vehicle:", err);
//       } finally {
//         setLoadingData(false);
//       }
//     };
//     loadVehicle();
//   }, [id]);

//   // ── Color handlers ──
//   const updateColor = (i, key, val) =>
//     setColors((prev) => prev.map((c, idx) => idx === i ? { ...c, [key]: val } : c));
//   const addColor    = () => setColors((p) => [...p, emptyColor()]);
//   const removeColor = (i) => setColors((p) => p.filter((_, idx) => idx !== i));

//   // ── Detail handlers ──
//   const updateDetail = (i, key, val) =>
//     setDetails((prev) => prev.map((d, idx) => idx === i ? { ...d, [key]: val } : d));
//   const addDetail    = () => setDetails((p) => [...p, emptyDetail()]);
//   const removeDetail = (i) => setDetails((p) => p.filter((_, idx) => idx !== i));

//   // ── Metric handlers ──
//   const updateMetric = (i, key, val) =>
//     setMetrics((prev) => prev.map((m, idx) => idx === i ? { ...m, [key]: val } : m));
//   const addMetric    = () => { if (metrics.length < 3) setMetrics((p) => [...p, emptyMetric()]); };
//   const removeMetric = (i) => setMetrics((p) => p.filter((_, idx) => idx !== i));

//   // ── Submit ──
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (colors.some((c) => !c.name.trim())) return alert("Fill all color names");
//     if (colors.some((c) => !c.imageUrl && !c.imageFile)) return alert("Each color needs an image");
//     if (details.some((d) => !d.label.trim() || !d.value.trim())) return alert("Fill all detail fields");
//     if (metrics.some((m) => !m.label.trim() || !m.value.trim())) return alert("Fill all metric fields");

//     setLoading(true);
//     try {
//       const uploadedColors = await Promise.all(
//         colors.map(async (c) => ({
//           id:       c.id || undefined,
//           name:     c.name,
//           hex:      c.hex,
//           imageUrl: c.imageFile ? await uploadToCloudinary(c.imageFile, "image") : c.imageUrl,
//           glbUrl:   c.glbFile   ? await uploadToCloudinary(c.glbFile,   "raw")   : c.glbUrl || null,
//         }))
//       );

//       const finalBrochureUrl = brochureFile
//         ? await uploadToCloudinary(brochureFile, "raw")
//         : brochureUrl || null;

//       const payload = {
//         categoryName,
//         variantName,
//         price: Number(price),
//         brochureUrl: finalBrochureUrl,
//         colors:  uploadedColors,
//         details: details.map(({ label, value, id: did }) => ({ label, value, ...(did ? { id: did } : {}) })),
//         metrics: metrics.map(({ label, value, id: mid }) => ({ label, value, ...(mid ? { id: mid } : {}) })),
//       };

//       const res = await fetch(`http://localhost:5000/api/vehicles/${id}`, {
//         method: "PUT",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       });

//       if (!res.ok) throw new Error("Server error");
//       alert("Vehicle updated successfully!");
//       navigate("/dashboard");
//     } catch (err) {
//       alert("Something went wrong. Please try again.");
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (loadingData) {
//     return (
//       <div className="ev-loading-screen">
//         <div className="ev-spinner" />
//         <p>Loading vehicle…</p>
//       </div>
//     );
//   }

//   return (
//     <div className="ev-page">

//       {/* Header */}
//       <div className="ev-header">
//         <div className="ev-header-left">
//           <button className="ev-back-btn" type="button" onClick={() => navigate("/vehicles")}>
//             <ArrowLeft size={16} />
//           </button>
//           <div className="ev-header-icon"><Car size={20} /></div>
//           <div>
//             <h1 className="ev-title">Edit Vehicle</h1>
//             <p className="ev-subtitle">ID #{id} — {variantName || "…"}</p>
//           </div>
//         </div>
//       </div>

//       <form onSubmit={handleSubmit} className="ev-form">

//         {/* ── 01 Basic Info ── */}
//         <div className="ev-section">
//           <div className="ev-section-label">
//             <span className="ev-section-num">01</span>
//             Basic Information
//           </div>
//           <div className="ev-section-body">
//             <div className="ev-row-3">

//               <div className="ev-field">
//                 <label className="ev-label">Category</label>
//                 <div className="ev-select-wrap">
//                   <select
//                     className="ev-select"
//                     value={categoryName}
//                     onChange={(e) => setCategoryName(e.target.value)}
//                   >
//                     <option value="n-first">n-First</option>
//                     <option value="diplos">Diplos</option>
//                     <option value="institutional">Institutional</option>
//                   </select>
//                   <ChevronDown size={14} className="ev-select-arrow" />
//                 </div>
//               </div>

//               <div className="ev-field">
//                 <label className="ev-label">Variant Name</label>
//                 <input
//                   className="ev-input"
//                   type="text"
//                   placeholder="e.g. i-max+"
//                   value={variantName}
//                   onChange={(e) => setVariantName(e.target.value)}
//                   required
//                 />
//               </div>

//               <div className="ev-field">
//                 <label className="ev-label">Price (₹)</label>
//                 <input
//                   className="ev-input"
//                   type="number"
//                   placeholder="e.g. 84999"
//                   value={price}
//                   onChange={(e) => setPrice(e.target.value)}
//                   required
//                 />
//               </div>

//             </div>
//           </div>
//         </div>

//         {/* ── 02 Colors ── */}
//         <div className="ev-section">
//           <div className="ev-section-label">
//             <span className="ev-section-num">02</span>
//             Colors &amp; Assets
//             <span className="ev-section-hint">Leave file blank to keep existing image / GLB</span>
//           </div>
//           <div className="ev-section-body">
//             <div className="ev-colors-list">
//               {colors.map((c, i) => (
//                 <div key={i} className="ev-color-card">

//                   {/* Existing image preview */}
//                   {c.imageUrl && (
//                     <div className="ev-color-thumb">
//                       <img src={c.imageUrl} alt={c.name} />
//                       <span className="ev-color-thumb-label">Current image</span>
//                     </div>
//                   )}

//                   <div className="ev-color-card-top">
//                     <div className="ev-color-swatch-wrap">
//                       <input
//                         type="color"
//                         className="ev-color-picker"
//                         value={c.hex}
//                         onChange={(e) => updateColor(i, "hex", e.target.value)}
//                       />
//                       <span className="ev-color-hex">{c.hex}</span>
//                     </div>
//                     <input
//                       className="ev-input ev-color-name"
//                       type="text"
//                       placeholder="Color name"
//                       value={c.name}
//                       onChange={(e) => updateColor(i, "name", e.target.value)}
//                     />
//                     {colors.length > 1 && (
//                       <button
//                         type="button"
//                         className="ev-icon-btn ev-danger"
//                         onClick={() => removeColor(i)}
//                         title="Remove color"
//                       >
//                         <Trash2 size={15} />
//                       </button>
//                     )}
//                   </div>

//                   <div className="ev-color-files">
//                     <div className="ev-file-field">
//                       <label className="ev-file-label">
//                         Replace Image <span className="ev-optional">optional</span>
//                       </label>
//                       <label className="ev-file-box">
//                         <input
//                           type="file"
//                           accept="image/*"
//                           className="ev-file-hidden"
//                           onChange={(e) => updateColor(i, "imageFile", e.target.files[0])}
//                         />
//                         <span className="ev-file-text">
//                           {c.imageFile ? c.imageFile.name : "Choose new image…"}
//                         </span>
//                         <span className="ev-file-btn">Browse</span>
//                       </label>
//                     </div>

//                     <div className="ev-file-field">
//                       <label className="ev-file-label">
//                         Replace 3D Model (.glb) <span className="ev-optional">optional</span>
//                       </label>
//                       <label className="ev-file-box">
//                         <input
//                           type="file"
//                           accept=".glb"
//                           className="ev-file-hidden"
//                           onChange={(e) => updateColor(i, "glbFile", e.target.files[0])}
//                         />
//                         <span className="ev-file-text">
//                           {c.glbFile ? c.glbFile.name : c.glbUrl ? "Current GLB saved" : "Choose .glb file…"}
//                         </span>
//                         <span className="ev-file-btn">Browse</span>
//                       </label>
//                     </div>
//                   </div>

//                 </div>
//               ))}
//             </div>

//             <button type="button" className="ev-add-btn" onClick={addColor}>
//               <Plus size={15} /> Add Color
//             </button>
//           </div>
//         </div>

//         {/* ── 03 Details ── */}
//         <div className="ev-section">
//           <div className="ev-section-label">
//             <span className="ev-section-num">03</span>
//             Specifications
//             <span className="ev-section-hint">Label–value pairs shown on the product card</span>
//           </div>
//           <div className="ev-section-body">
//             <div className="ev-kv-list">
//               {details.map((d, i) => (
//                 <div key={i} className="ev-kv-row">
//                   <input
//                     className="ev-input"
//                     type="text"
//                     placeholder="Label (e.g. Battery Capacity)"
//                     value={d.label}
//                     onChange={(e) => updateDetail(i, "label", e.target.value)}
//                   />
//                   <input
//                     className="ev-input"
//                     type="text"
//                     placeholder="Value (e.g. 3 kWh)"
//                     value={d.value}
//                     onChange={(e) => updateDetail(i, "value", e.target.value)}
//                   />
//                   {details.length > 1 && (
//                     <button
//                       type="button"
//                       className="ev-icon-btn ev-danger"
//                       onClick={() => removeDetail(i)}
//                     >
//                       <Trash2 size={15} />
//                     </button>
//                   )}
//                 </div>
//               ))}
//             </div>
//             <button type="button" className="ev-add-btn" onClick={addDetail}>
//               <Plus size={15} /> Add Specification
//             </button>
//           </div>
//         </div>

//         {/* ── 04 Metrics ── */}
//         <div className="ev-section">
//           <div className="ev-section-label">
//             <span className="ev-section-num">04</span>
//             Key Metrics
//             <span className="ev-section-hint">Max 3 highlights</span>
//           </div>
//           <div className="ev-section-body">
//             <div className="ev-kv-list">
//               {metrics.map((m, i) => (
//                 <div key={i} className="ev-kv-row">
//                   <input
//                     className="ev-input"
//                     type="text"
//                     placeholder="Label (e.g. IDC Range)"
//                     value={m.label}
//                     onChange={(e) => updateMetric(i, "label", e.target.value)}
//                   />
//                   <input
//                     className="ev-input"
//                     type="text"
//                     placeholder="Value (e.g. 109 km)"
//                     value={m.value}
//                     onChange={(e) => updateMetric(i, "value", e.target.value)}
//                   />
//                   {metrics.length > 1 && (
//                     <button
//                       type="button"
//                       className="ev-icon-btn ev-danger"
//                       onClick={() => removeMetric(i)}
//                     >
//                       <Trash2 size={15} />
//                     </button>
//                   )}
//                 </div>
//               ))}
//             </div>
//             {metrics.length < 3 && (
//               <button type="button" className="ev-add-btn" onClick={addMetric}>
//                 <Plus size={15} /> Add Metric
//               </button>
//             )}
//           </div>
//         </div>

//         {/* ── 05 Brochure ── */}
//         <div className="ev-section">
//           <div className="ev-section-label">
//             <span className="ev-section-num">05</span>
//             Brochure PDF
//             <span className="ev-section-hint">Upload new file to replace existing</span>
//           </div>
//           <div className="ev-section-body">
//             {brochureUrl && !brochureFile && (
//               <div className="ev-existing-brochure">
//                 <a href={brochureUrl} target="_blank" rel="noreferrer">
//                   View current brochure ↗
//                 </a>
//               </div>
//             )}
//             <div className="ev-file-field ev-file-wide">
//               <label className="ev-file-label">
//                 {brochureUrl ? "Replace PDF" : "Upload PDF"}
//                 <span className="ev-optional"> optional</span>
//               </label>
//               <label className="ev-file-box">
//                 <input
//                   type="file"
//                   accept=".pdf"
//                   className="ev-file-hidden"
//                   onChange={(e) => setBrochureFile(e.target.files[0])}
//                 />
//                 <span className="ev-file-text">
//                   {brochureFile ? brochureFile.name : "Choose PDF file…"}
//                 </span>
//                 <span className="ev-file-btn">Browse</span>
//               </label>
//             </div>
//           </div>
//         </div>

//         {/* ── Footer ── */}
//         <div className="ev-footer">
//           <button
//             type="button"
//             className="ev-cancel-btn"
//             onClick={() => navigate("/vehicles")}
//           >
//             Cancel
//           </button>
//           <button type="submit" className="ev-submit" disabled={loading}>
//             {loading ? (
//               <><span className="ev-spinner-sm" /> Uploading &amp; Saving…</>
//             ) : (
//               <><Save size={16} /> Update Vehicle</>
//             )}
//           </button>
//         </div>

//       </form>
//     </div>
//   );
// }
